/** Shared primitives for case-insensitive substring search ranking and highlighting. */

/** How well a value matches a search term. Lower is better. */
export const MatchTier = {
  Exact: 0,
  StartsWith: 1,
  EndsWith: 2,
  Contains: 3,
} as const;
export type MatchTier = (typeof MatchTier)[keyof typeof MatchTier];

/**
 * Classifies a value that is already known to contain `search` at `matchIndex`.
 * `value` and `search` must already be normalized to the same case.
 */
export function getMatchTier(value: string, search: string, matchIndex: number): MatchTier {
  if (value === search) {
    return MatchTier.Exact;
  }
  if (matchIndex === 0) {
    return MatchTier.StartsWith;
  }
  if (value.endsWith(search)) {
    return MatchTier.EndsWith;
  }
  return MatchTier.Contains;
}

/**
 * Inclusive `[start, end]` ranges for every non-overlapping, case-insensitive occurrence of
 * `search` in `value`, so highlighting covers all matched ranges rather than only the first.
 * Returns `[]` when either side is missing or empty, or when there is no match, so callers can
 * pass optional model fields straight through at render time.
 */
export function getSubstringMatchIndices(
  value: string | null | undefined,
  search: string | null | undefined
): [number, number][] {
  if (!value || !search) {
    return [];
  }

  const normalizedValue = value.toLowerCase();
  const normalizedSearch = search.toLowerCase();
  const indices: [number, number][] = [];

  for (
    let matchIndex = normalizedValue.indexOf(normalizedSearch);
    matchIndex >= 0;
    matchIndex = normalizedValue.indexOf(normalizedSearch, matchIndex + normalizedSearch.length)
  ) {
    indices.push([matchIndex, matchIndex + normalizedSearch.length - 1]);
  }

  return indices;
}

function mergeMatchRanges(ranges: [number, number][]): [number, number][] {
  const merged: [number, number][] = [];

  for (const [start, end] of ranges.toSorted(([aStart, aEnd], [bStart, bEnd]) => aStart - bStart || aEnd - bEnd)) {
    const previous = merged.at(-1);
    if (previous == null || start > previous[1] + 1) {
      merged.push([start, end]);
    } else if (end > previous[1]) {
      previous[1] = end;
    }
  }

  return merged;
}

/** Groups sorted character positions into inclusive ranges, splitting where a character was skipped. */
function rangesFromPositions(positions: readonly number[]): [number, number][] {
  const ranges: [number, number][] = [];
  let start = positions[0];
  let end = positions[0];

  for (let i = 1; i < positions.length; i++) {
    const position = positions[i];
    if (position === end + 1) {
      end = position;
    } else {
      ranges.push([start, end]);
      start = position;
      end = position;
    }
  }

  ranges.push([start, end]);
  return ranges;
}

/**
 * Maps a match in the hyphen-stripped label back onto the original characters.
 * "ethusd" in "ETH-USDT" highlights ETH and USD, not the hyphen or the trailing T.
 */
function getHyphenSkippedMatchIndices(value: string, search: string): [number, number][] {
  const normalizedSearch = search.toLowerCase().replaceAll('-', '');
  if (normalizedSearch.length === 0) {
    return [];
  }

  const originalPositions: number[] = [];
  let stripped = '';
  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    if (char === '-') {
      continue;
    }
    originalPositions.push(i);
    stripped += char.toLowerCase();
  }

  const ranges: [number, number][] = [];
  for (
    let matchIndex = stripped.indexOf(normalizedSearch);
    matchIndex >= 0;
    matchIndex = stripped.indexOf(normalizedSearch, matchIndex + normalizedSearch.length)
  ) {
    ranges.push(...rangesFromPositions(originalPositions.slice(matchIndex, matchIndex + normalizedSearch.length)));
  }

  return ranges;
}

/**
 * Highlight ranges for a symbol label. A contiguous match is used as-is. Otherwise a hyphenless
 * query is aligned to the label with hyphens skipped, so only the matched letters are highlighted.
 */
export function getSymbolDisplayMatchIndices(
  value: string | null | undefined,
  terms: readonly string[]
): [number, number][] {
  if (!value || terms.length === 0) {
    return [];
  }

  return mergeMatchRanges(
    terms.flatMap(term => {
      const direct = getSubstringMatchIndices(value, term);
      return direct.length > 0 ? direct : getHyphenSkippedMatchIndices(value, term);
    })
  );
}

/** Inclusive ranges for every term, merged where the matches overlap or sit next to each other. */
export function getSubstringMatchIndicesForTerms(
  value: string | null | undefined,
  terms: readonly string[]
): [number, number][] {
  if (!value || terms.length === 0) {
    return [];
  }

  return mergeMatchRanges(terms.flatMap(term => getSubstringMatchIndices(value, term)));
}
